// /frontend/src/components/rfq/ProposalGenerator.jsx
import React, { useState } from 'react';
import { FaDownload, FaPrint, FaCalendarAlt, FaClock } from 'react-icons/fa';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ProposalGenerator = ({ cart, userInfo, quoteRequestId }) => {
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Timeline configuration
  const getProjectTimeline = () => {
    const today = new Date();
    
    return {
      quoteSubmission: {
        title: "Quote Submission",
        description: "Initial quote request submitted",
        date: today,
        duration: "Day 0",
        status: "completed",
        phase: "planning"
      },
      quoteReview: {
        title: "Quote Review & Approval",
        description: "Our team reviews requirements and provides detailed quotation",
        date: addBusinessDays(today, 2),
        duration: "1-2 Business Days",
        status: "pending",
        phase: "planning"
      },
      clientApproval: {
        title: "Client Approval",
        description: "Client reviews and approves the quotation",
        date: addBusinessDays(today, 5),
        duration: "2-3 Business Days",
        status: "upcoming",
        phase: "planning"
      },
      paymentProcessing: {
        title: "Payment Processing",
        description: "Initial payment processing and order confirmation",
        date: addBusinessDays(today, 7),
        duration: "1-2 Business Days",
        status: "upcoming",
        phase: "payment"
      },
      designPhase: {
        title: "Design & Planning Phase",
        description: "Detailed design work, technical specifications, and project planning",
        date: addBusinessDays(today, 10),
        duration: "5-10 Business Days",
        status: "upcoming",
        phase: "design"
      },
      productionPhase: {
        title: "Production Phase",
        description: "Manufacturing, sourcing, and quality control of all components",
        date: addBusinessDays(today, 20),
        duration: "10-15 Business Days",
        status: "upcoming",
        phase: "production"
      },
      qualityAssurance: {
        title: "Quality Assurance",
        description: "Final quality checks, testing, and packaging",
        date: addBusinessDays(today, 33),
        duration: "2-3 Business Days",
        status: "upcoming",
        phase: "production"
      },
      delivery: {
        title: "Delivery & Logistics",
        description: "Shipping coordination and delivery to specified location",
        date: addBusinessDays(today, 38),
        duration: "3-5 Business Days",
        status: "upcoming",
        phase: "delivery"
      },
      installation: {
        title: "Installation & Setup",
        description: "On-site installation, configuration, and testing",
        date: addBusinessDays(today, 43),
        duration: "1-3 Business Days",
        status: "upcoming",
        phase: "installation"
      },
      handover: {
        title: "Project Handover",
        description: "Final walkthrough, documentation, and project completion",
        date: addBusinessDays(today, 46),
        duration: "1 Business Day",
        status: "upcoming",
        phase: "completion"
      },
      warranty: {
        title: "Warranty Period Begins",
        description: "12-month comprehensive warranty and support period",
        date: addBusinessDays(today, 47),
        duration: "12 Months",
        status: "upcoming",
        phase: "support"
      }
    };
  };

  // Helper function to add business days
  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let addedDays = 0;
    
    while (addedDays < days) {
      result.setDate(result.getDate() + 1);
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        addedDays++;
      }
    }
    
    return result;
  };

  // Get phase colors
  const getPhaseColor = (phase) => {
    const colors = {
      planning: 'text-blue-600 bg-blue-50 border-blue-200',
      payment: 'text-green-600 bg-green-50 border-green-200',
      design: 'text-purple-600 bg-purple-50 border-purple-200',
      production: 'text-orange-600 bg-orange-50 border-orange-200',
      delivery: 'text-indigo-600 bg-indigo-50 border-indigo-200',
      installation: 'text-red-600 bg-red-50 border-red-200',
      completion: 'text-teal-600 bg-teal-50 border-teal-200',
      support: 'text-gray-600 bg-gray-50 border-gray-200'
    };
    return colors[phase] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return (
          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'pending':
        return (
          <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center">
            <FaClock className="w-3 h-3 text-white" />
          </div>
        );
      default:
        return (
          <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-white"></div>
          </div>
        );
    }
  };

  // Generate proposal content
  const generateProposalHTML = () => {
    const timeline = getProjectTimeline();
    const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    
    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
        <!-- Header -->
        <div style="text-align: center; border-bottom: 2px solid #1f2937; padding-bottom: 20px; margin-bottom: 30px;">
          <h1 style="color: #1f2937; margin-bottom: 10px;">PROJECT PROPOSAL</h1>
          <h2 style="color: #6b7280; font-weight: normal; margin: 0;">Henderson Design Group</h2>
          <p style="color: #6b7280; margin: 5px 0;">Design & Manufacturing Solutions</p>
        </div>

        <!-- Client Information -->
        <div style="margin-bottom: 30px;">
          <h3 style="color: #1f2937; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">Client Information</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 15px;">
            <div>
              <p><strong>Client Name:</strong> ${userInfo?.name || 'N/A'}</p>
              <p><strong>Email:</strong> ${userInfo?.email || 'N/A'}</p>
              <p><strong>Quote Request ID:</strong> ${quoteRequestId}</p>
            </div>
            <div>
              <p><strong>Date Generated:</strong> ${new Date().toLocaleDateString()}</p>
              <p><strong>Total Items:</strong> ${totalItems} item(s)</p>
              <p><strong>Project Type:</strong> Design & Manufacturing</p>
            </div>
          </div>
        </div>

        <!-- Project Overview -->
        <div style="margin-bottom: 30px;">
          <h3 style="color: #1f2937; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">Project Overview</h3>
          <div style="background: #f9fafb; padding: 20px; border-left: 4px solid #3b82f6; margin-top: 15px;">
            <p style="margin: 0; line-height: 1.6;">
              This proposal outlines our comprehensive approach to designing, manufacturing, and delivering your custom project. 
              Our team will work closely with you throughout each phase to ensure the highest quality results that meet your 
              specific requirements and exceed your expectations.
            </p>
          </div>
        </div>

        <!-- Items Requested -->
        <div style="margin-bottom: 30px;">
          <h3 style="color: #1f2937; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">Items Requested for Quote</h3>
          <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
            <thead>
              <tr style="background: #f9fafb;">
                <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: left;">Product</th>
                <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: center;">Quantity</th>
                <th style="border: 1px solid #e5e7eb; padding: 12px; text-align: center;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${cart.items.map(item => `
                <tr>
                  <td style="border: 1px solid #e5e7eb; padding: 12px;">
                    <strong>${item.product.name}</strong>
                    ${item.variants && Object.keys(item.variants).length > 0 ? 
                      '<br><small style="color: #6b7280;">' + 
                      Object.entries(item.variants).map(([key, value]) => `${key}: ${value}`).join(', ') + 
                      '</small>' : ''}
                  </td>
                  <td style="border: 1px solid #e5e7eb; padding: 12px; text-align: center;">${item.quantity}</td>
                  <td style="border: 1px solid #e5e7eb; padding: 12px; text-align: center;">
                    <span style="background: #fef3c7; color: #92400e; padding: 4px 8px; border-radius: 12px; font-size: 12px;">
                      Quote Pending
                    </span>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <!-- Project Timeline -->
        <div style="margin-bottom: 30px;">
          <h3 style="color: #1f2937; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">Project Timeline & Milestones</h3>
          <p style="color: #6b7280; margin-top: 10px;">
            The following timeline provides an estimated schedule for your project from initial approval through completion:
          </p>
          
          <div style="margin-top: 20px;">
            ${Object.entries(timeline).map(([key, milestone], index) => `
              <div style="display: flex; margin-bottom: 20px; ${milestone.status === 'completed' ? 'opacity: 1' : milestone.status === 'pending' ? 'opacity: 0.9' : 'opacity: 0.7'};">
                <div style="flex-shrink: 0; margin-right: 15px; margin-top: 3px;">
                  <div style="width: 24px; height: 24px; border-radius: 50%; background: ${milestone.status === 'completed' ? '#10b981' : milestone.status === 'pending' ? '#f59e0b' : '#d1d5db'}; display: flex; align-items: center; justify-content: center;">
                    ${milestone.status === 'completed' ? '✓' : (index + 1)}
                  </div>
                  ${index < Object.keys(timeline).length - 1 ? '<div style="width: 2px; height: 40px; background: #e5e7eb; margin-left: 11px; margin-top: 4px;"></div>' : ''}
                </div>
                <div style="flex: 1;">
                  <div style="display: flex; justify-content: between; align-items: start; margin-bottom: 8px;">
                    <div style="flex: 1;">
                      <h4 style="margin: 0; color: #1f2937; font-size: 16px;">${milestone.title}</h4>
                      <p style="margin: 4px 0; color: #6b7280; font-size: 14px;">${milestone.description}</p>
                    </div>
                    <div style="text-align: right; margin-left: 20px;">
                      <div style="background: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-size: 12px; color: #374151; margin-bottom: 4px;">
                        ${milestone.phase.toUpperCase()}
                      </div>
                      <div style="font-size: 12px; color: #6b7280;">${milestone.date.toLocaleDateString()}</div>
                      <div style="font-size: 11px; color: #9ca3af;">${milestone.duration}</div>
                    </div>
                  </div>
                </div>
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Key Project Phases -->
        <div style="margin-bottom: 30px;">
          <h3 style="color: #1f2937; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">Key Project Phases</h3>
          
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; margin-top: 20px;">
            <div style="background: #eff6ff; border: 1px solid #dbeafe; border-radius: 8px; padding: 16px;">
              <h4 style="color: #1e40af; margin: 0 0 8px 0;">Planning & Design</h4>
              <p style="color: #374151; font-size: 14px; margin: 0; line-height: 1.5;">
                Comprehensive requirement analysis, technical specifications, and detailed design development.
              </p>
            </div>
            
            <div style="background: #f0fdf4; border: 1px solid #dcfce7; border-radius: 8px; padding: 16px;">
              <h4 style="color: #166534; margin: 0 0 8px 0;">Production</h4>
              <p style="color: #374151; font-size: 14px; margin: 0; line-height: 1.5;">
                High-quality manufacturing using premium materials and advanced production techniques.
              </p>
            </div>
            
            <div style="background: #fef7ff; border: 1px solid #f3e8ff; border-radius: 8px; padding: 16px;">
              <h4 style="color: #7c3aed; margin: 0 0 8px 0;">Quality Assurance</h4>
              <p style="color: #374151; font-size: 14px; margin: 0; line-height: 1.5;">
                Rigorous testing and quality control measures to ensure product excellence.
              </p>
            </div>
            
            <div style="background: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px; padding: 16px;">
              <h4 style="color: #c2410c; margin: 0 0 8px 0;">Delivery & Installation</h4>
              <p style="color: #374151; font-size: 14px; margin: 0; line-height: 1.5;">
                Professional delivery, installation, and comprehensive setup at your location.
              </p>
            </div>
          </div>
        </div>

        <!-- Terms & Conditions -->
        <div style="margin-bottom: 30px;">
          <h3 style="color: #1f2937; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px;">Terms & Conditions</h3>
          <div style="margin-top: 15px; font-size: 14px; line-height: 1.6;">
            <ul style="color: #374151; padding-left: 20px;">
              <li style="margin-bottom: 8px;"><strong>Timeline:</strong> All dates are estimated and subject to client approval and material availability.</li>
              <li style="margin-bottom: 8px;"><strong>Payment:</strong> Payment terms will be specified in the final quotation upon approval.</li>
              <li style="margin-bottom: 8px;"><strong>Changes:</strong> Any changes to specifications may affect timeline and pricing.</li>
              <li style="margin-bottom: 8px;"><strong>Warranty:</strong> 12-month comprehensive warranty on all manufactured components.</li>
              <li style="margin-bottom: 8px;"><strong>Validity:</strong> This proposal is valid for 30 days from the date of issue.</li>
            </ul>
          </div>
        </div>

        <!-- Contact Information -->
        <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin-top: 30px;">
          <h3 style="color: #1f2937; margin: 0 0 15px 0;">Contact Information</h3>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px;">
            <div>
              <p style="margin: 0; color: #374151;"><strong>Project Manager:</strong> Henderson Design Team</p>
              <p style="margin: 4px 0; color: #374151;"><strong>Email:</strong> quotes@hendersondesign.com</p>
              <p style="margin: 4px 0; color: #374151;"><strong>Phone:</strong> +1 (555) 123-4567</p>
            </div>
            <div>
              <p style="margin: 0; color: #374151;"><strong>Office Hours:</strong> Mon-Fri 9:00 AM - 6:00 PM</p>
              <p style="margin: 4px 0; color: #374151;"><strong>Response Time:</strong> Within 24-48 hours</p>
              <p style="margin: 4px 0; color: #374151;"><strong>Support:</strong> Dedicated project support</p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
          <p style="color: #6b7280; font-size: 12px; margin: 0;">
            Henderson Design Group | Design & Manufacturing Solutions
          </p>
          <p style="color: #6b7280; font-size: 12px; margin: 4px 0 0 0;">
            Generated on ${new Date().toLocaleDateString()} | Proposal ID: ${quoteRequestId}
          </p>
        </div>
      </div>
    `;
  };

  // Generate PDF
  const generatePDF = async () => {
    setGenerating(true);
    
    try {
      // Create temporary div with proposal content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = generateProposalHTML();
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '800px';
      document.body.appendChild(tempDiv);

      // Convert to canvas
      const canvas = await html2canvas(tempDiv, {
        width: 800,
        height: tempDiv.scrollHeight,
        scale: 2,
        logging: false
      });

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');
      
      const imgWidth = 190;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 10;

      // Add first page
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 20;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download PDF
      pdf.save(`Henderson_Design_Proposal_${quoteRequestId}.pdf`);

      // Clean up
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Timeline component for preview
  const TimelinePreview = () => {
    const timeline = getProjectTimeline();
    
    return (
      <div className="space-y-6">
        {Object.entries(timeline).map(([key, milestone], index) => (
          <div key={key} className="flex items-start space-x-4">
            <div className="flex flex-col items-center">
              {getStatusIcon(milestone.status)}
              {index < Object.keys(timeline).length - 1 && (
                <div className="w-px h-12 bg-gray-200 mt-2"></div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">{milestone.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
                </div>
                
                <div className="text-right ml-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPhaseColor(milestone.phase)}`}>
                    {milestone.phase.toUpperCase()}
                  </span>
                  <div className="text-sm text-gray-500 mt-1">
                    {milestone.date.toLocaleDateString()}
                  </div>
                  <div className="text-xs text-gray-400">
                    {milestone.duration}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Project Proposal</h3>
          <p className="text-sm text-gray-600 mt-1">
            Download a comprehensive proposal with project timeline and specifications
          </p>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <FaCalendarAlt className="mr-2 h-4 w-4" />
            {showPreview ? 'Hide Timeline' : 'View Timeline'}
          </button>
          
          <button
            onClick={generatePDF}
            disabled={generating}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
          >
            {generating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <FaDownload className="mr-2 h-4 w-4" />
                Download Proposal
              </>
            )}
          </button>
        </div>
      </div>

      {/* Timeline Preview */}
      {showPreview && (
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Project Timeline Preview</h4>
          <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
            <TimelinePreview />
          </div>
        </div>
      )}

      {/* Summary Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {cart.items.reduce((sum, item) => sum + item.quantity, 0)}
          </div>
          <div className="text-sm text-gray-600">Total Items</div>
        </div>
        
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">~45</div>
          <div className="text-sm text-gray-600">Business Days</div>
        </div>
        
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">11</div>
          <div className="text-sm text-gray-600">Project Phases</div>
        </div>
      </div>
    </div>
  );
};

export default ProposalGenerator;